import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useMutation } from "@tanstack/react-query";
import { CBAuth, CustomerBaseDto } from "../../types";
import { isGlobalEditModeState } from "../../state/customerbaseState";
import { unlinkDigitalIdentity } from "../../api/service/customerbaseApi";
import { useHasScope } from "../../../../config/globalConfig";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import CustomerBaseModal from "../modal/CustomerBaseModal";

/**
 * CR-DRIVALIA-EPCH (LPP-22837): sgancio dell'identita' digitale (idpUid) da System Console.
 * Azione con conferma, disponibile solo se il customer ha un idpUid; disabilitata mentre e' aperta
 * la modifica generale, per non sovrapporre due scritture sullo stesso customer.
 * Restituisce lo stato per l'azione in coda alla chip e la modale da rendere accanto.
 */
export const useDigitalIdentityUnlink = (customer: CustomerBaseDto | null) => {
    const { t } = useTranslation("customerbase");
    const hasScope = useHasScope;
    const { dispatchGlobalData } = useContext(GlobalContext);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const [showConfirm, setShowConfirm] = useState(false);
    // Da conferma a ricaricamento della pagina: la modale resta aperta con i pulsanti disabilitati e lo
    // spinner su "Sgancia". Non basta isLoading della mutation, che torna false prima del reload.
    const [pending, setPending] = useState(false);

    const stopWithError = (message: string) => {
        setPending(false);
        setShowConfirm(false);
        showError(message);
    };

    const showError = (message: string) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message,
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                },
            },
        });
    };

    const unlinkMutation = useMutation(unlinkDigitalIdentity, {
        onError: () => stopWithError(t("digitalIdentityUnlink.error")),
        onSuccess: (response: any) => {
            // CB inghiotte gli errori del blocco che sgancia l'idpUid: l'esito si verifica sul dato.
            if (response?.data?.idpUid) {
                stopWithError(t("digitalIdentityUnlink.notApplied"));
                return;
            }
            window.location.reload();
        }
    });

    const visible = !!customer?.cbCustomerId && !!customer?.idpUid && hasScope(CBAuth.WRITE_CUSTOMER);
    const disabled = isGlobalEditMode || pending;

    return {
        visible,
        disabled,
        tooltip: isGlobalEditMode ? t("digitalIdentityUnlink.disabledWhileEditing") : t("digitalIdentityUnlink.tooltip"),
        request: () => {
            if (!disabled) setShowConfirm(true);
        },
        modal: visible ? (
            <CustomerBaseModal
                open={showConfirm}
                contentDescription={t("digitalIdentityUnlink.confirmText")}
                buttonCloseDescription={t("editingModal.buttons.close")}
                onClose={() => {
                    if (!pending) setShowConfirm(false);
                }}
                buttonConfirmDescription={t("digitalIdentityUnlink.confirmButton")}
                confirmLoading={pending}
                confirmLoadingDescription={t("digitalIdentityUnlink.inProgress")}
                onConfirm={() => {
                    if (pending) return;
                    setPending(true);
                    unlinkMutation.mutate(customer!.cbCustomerId);
                }}
            />
        ) : null,
    };
};
